import React from "react";
import { statusMeta } from "@/lib/format";

function polar(cx, cy, r, angleDeg) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
// semicircle arc from 180deg (left) to 0deg (right), sweeping over the top
function arcPath(cx, cy, r, fromDeg, toDeg) {
  const start = polar(cx, cy, r, fromDeg);
  const end = polar(cx, cy, r, toDeg);
  return `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;
}

// full-circle ring gauge (donut) with centered value + label
export const RingGauge = ({ value = 0, status, size = 180, stroke = 16, label = "CONFIDENCE" }) => {
  const r = (size - stroke) / 2 - 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value ?? 0)) / 100;
  const m = statusMeta(status || (value >= 90 ? "READY" : value >= 70 ? "AT_RISK" : "NOT_READY"));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#0F2043" strokeOpacity="0.12" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={m.color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.7s cubic-bezier(0.4,0,0.2,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="tnum font-bold text-cnp-navy" style={{ fontSize: size * 0.24 }}>{value == null ? "N/A" : `${Math.round(value)}%`}</span>
        <span className="uppercase tracking-wide text-cnp-textMuted" style={{ fontSize: size * 0.07, fontWeight: 600 }}>{label}</span>
      </div>
    </div>
  );
};


export const GaugeRadial = ({ value = 0, status, size = 220, stroke = 14, label, showEnds = true }) => {
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - stroke) / 2 - 4;
  const pct = Math.max(0, Math.min(100, value ?? 0)) / 100;
  const m = statusMeta(status || (value >= 90 ? "READY" : value >= 70 ? "AT_RISK" : "NOT_READY"));
  // sweep 180deg (from 180 -> 360) over the top
  const startAngle = 180;
  const valueAngle = 180 + pct * 180;
  const height = cy + 8;

  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <svg width={size} height={height} className="overflow-visible">
        <path d={arcPath(cx, cy, r, 180, 360)} fill="none" stroke="#E3E8EF" strokeWidth={stroke} strokeLinecap="round" />
        <path
          d={arcPath(cx, cy, r, startAngle, valueAngle)}
          fill="none" stroke={m.color} strokeWidth={stroke} strokeLinecap="round"
          style={{ transition: "all 0.7s cubic-bezier(0.4,0,0.2,1)" }}
        />
        <text x={cx} y={cy - 6} textAnchor="middle" className="tnum"
          style={{ fontSize: size * 0.2, fontWeight: 700, fill: "#1F2937" }}>
          {value == null ? "N/A" : `${Math.round(value)}%`}
        </text>
        {label && (
          <text x={cx} y={cy + size * 0.11} textAnchor="middle"
            style={{ fontSize: size * 0.058, fontWeight: 600, fill: m.text, letterSpacing: "0.05em" }}>
            {label}
          </text>
        )}
        {showEnds && (
          <>
            <text x={polar(cx, cy, r, 180).x} y={cy + 16} textAnchor="middle" style={{ fontSize: 11, fill: "#9BA3AF" }}>0%</text>
            <text x={polar(cx, cy, r, 360).x} y={cy + 16} textAnchor="middle" style={{ fontSize: 11, fill: "#9BA3AF" }}>100%</text>
          </>
        )}
      </svg>
    </div>
  );
};
